/**
 * Monitoring stubs.
 *
 * Two functions: `reportError` for thrown exceptions / rejected
 * promises, and `reportEvent` for product-funnel attributes
 * (sign-up, first save, download, etc.). The default impl writes to
 * `console.error` / `console.log` so localhost development still
 * produces visible signal; Phase 6b swaps a real provider (Sentry,
 * Firebase Crashlytics, Amplitude — TBD) by reassigning the two
 * exports.
 *
 * This module deliberately has zero dependencies. Loading it on the
 * first frame must be free.
 *
 * The provider seam is a `setReporter` function rather than a class
 * because:
 *   - The two reporters are call sites, not a class instance — every
 *     consumer wants the same singleton.
 *   - A function-replacement shim lets the Phase 6b provider commit
 *     a single line in `main.tsx` (`setReporter(sentryReporter)`)
 *     without touching every caller.
 *   - It composes naturally with React's strict-mode double-mount —
 *     the reporter is referentially stable across re-renders.
 */

/**
 * The shape every reporter must satisfy. Either field can be a
 * stub; `attrs` is forwarded as-is so the eventual provider can map
 * it into Sentry tags / Crashlytics keys / Amplitude properties
 * without imposing a key vocabulary now.
 */
export interface IMonitoringReporter {
  reportError(err: unknown, context?: Readonly<Record<string, unknown>>): void;
  reportEvent(name: string, attrs?: Readonly<Record<string, unknown>>): void;
}

/**
 * Default reporter: console-only. Formats the error message + stack
 * if available; falls back to `String(err)` for non-Error rejections.
 */
const consoleReporter: IMonitoringReporter = {
  reportError(err, context) {
    // eslint-disable-next-line no-console
    console.error('[monitoring] error', err, context ?? {});
  },
  reportEvent(name, attrs) {
    // eslint-disable-next-line no-console
    console.log('[monitoring] event', name, attrs ?? {});
  },
};

let activeReporter: IMonitoringReporter = consoleReporter;

/**
 * Swap the active reporter. Phase 6b's Sentry / Crashlytics
 * integration calls this once from `main.tsx`, before any UI
 * renders, so subsequent `reportError` / `reportEvent` calls land
 * on the real provider.
 */
export function setMonitoringReporter(reporter: IMonitoringReporter): void {
  activeReporter = reporter;
}

/** Reset to the console reporter — test-only. */
export function __resetMonitoringReporterForTests(): void {
  activeReporter = consoleReporter;
}

/**
 * Report an error with optional context. The context object is
 * forwarded to the active reporter unchanged.
 *
 * Use sparingly: monitoring noise drowns real signal. Reserve this
 * for caught exceptions in long-lived async paths (auth, persist,
 * upload) — not for control-flow that the UI already surfaces.
 */
export function reportError(
  err: unknown,
  context?: Readonly<Record<string, unknown>>,
): void {
  activeReporter.reportError(err, context);
}

/**
 * Report a product event. `name` is a stable key (e.g.
 * `'applicant.signed_in'`); `attrs` is a flat bag of strings /
 * numbers / booleans the eventual provider can index.
 */
export function reportEvent(
  name: string,
  attrs?: Readonly<Record<string, unknown>>,
): void {
  activeReporter.reportEvent(name, attrs);
}

/**
 * Install a process-wide handler that funnels uncaught errors and
 * unhandled promise rejections into `reportError`. Call once from
 * `main.tsx` before mounting the React tree. Idempotent — calling
 * twice replaces the prior listener so HMR doesn't stack handlers.
 *
 * MDN references:
 *   https://developer.mozilla.org/en-US/docs/Web/API/Window/error_event
 *   https://developer.mozilla.org/en-US/docs/Web/API/Window/unhandledrejection_event
 */
let installedListeners: {
  onError: (e: ErrorEvent) => void;
  onRejection: (e: PromiseRejectionEvent) => void;
} | null = null;

export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;
  if (installedListeners) {
    window.removeEventListener('error', installedListeners.onError);
    window.removeEventListener(
      'unhandledrejection',
      installedListeners.onRejection,
    );
  }
  const onError = (e: ErrorEvent): void => {
    reportError(e.error ?? e.message, {
      kind: 'window.error',
      filename: e.filename,
      lineno: e.lineno,
      colno: e.colno,
    });
  };
  const onRejection = (e: PromiseRejectionEvent): void => {
    reportError(e.reason, { kind: 'unhandledrejection' });
  };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  installedListeners = { onError, onRejection };
}
